<template>
  <div class="w-full flex flex-col">
    <div class="mb-2.5">
      <p class="text-sm text-[#58595b] mb-2.5 text-center">
        °F = (1.8 x °C) + 32 <br />
        °C = (°F - 32) / 1.8
      </p>
      <div class="font-bold text-center">Fill in one value to calculate the other</div>
    </div>
    <div class="w-full flex flex-col gap-1">
      <div class="input-block w-full flex flex-col gap-0.5">
        <div class="w-full text-sm text-gray">°F</div>
        <label
          :for="`${calcType}-${CalculatorFieldsTypes.fahrenheit}-input`"
          class="w-full">
          <input
            type="number"
            :id="`${calcType}-${CalculatorFieldsTypes.fahrenheit}-input`"
            :title="`${calcType}-${CalculatorFieldsTypes.fahrenheit}-input`"
            placeholder=""
            class="w-full h-8 border border-black text-sm text-gray py-2 px-4 rounded-full"
            :class="{ 'border-blue-600 bg-yellow-100': state.inputFields.includes(CalculatorFieldsTypes.fahrenheit) }"
            v-model="state.fahrenheit"
            @input="updateInputAndOutputFields(CalculatorFieldsTypes.fahrenheit, state.fahrenheit)" />
        </label>
      </div>
      <div class="input-block w-full flex flex-col gap-0.5">
        <div class="w-full text-sm text-gray">°C</div>
        <label
          :for="`${calcType}-${CalculatorFieldsTypes.celsius}-input`"
          class="w-full">
          <input
            type="number"
            :id="`${calcType}-${CalculatorFieldsTypes.celsius}-input`"
            :title="`${calcType}-${CalculatorFieldsTypes.celsius}-input`"
            placeholder=""
            class="w-full h-8 border border-black text-sm text-gray py-2 px-4 rounded-full"
            :class="{ 'border-blue-600 bg-yellow-100': state.inputFields.includes(CalculatorFieldsTypes.celsius) }"
            v-model="state.celsius"
            @input="updateInputAndOutputFields(CalculatorFieldsTypes.celsius, state.celsius)" />
        </label>
      </div>
      <div class="w-full flex items-center justify-center px-4 gap-2.5 mt-1">
        <div
          class="text-sm py-1.5 px-3 rounded text-white bg-red hover:bg-dark-red cursor-pointer flex items-center justify-center transition-colors duration-300"
          :class="{ 'grayscale pointer-events-none': state.inputFields.length === 0 }"
          @click="resetAction">
          Reset
        </div>
        <div
          class="w-full max-w-44 text-sm py-1.5 px-3 rounded text-white bg-red hover:bg-dark-red cursor-pointer flex items-center justify-center transition-colors duration-300"
          :class="{ 'grayscale pointer-events-none': state.inputFields.length < minInputFieldsQty }"
          @click="calculateAction">
          Calculate
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive } from 'vue';
import CalculatorsHelper from '@/helpers/global/calculators.helper';
import GlobalHelper from '@/helpers/global/global.helper';

enum CalculatorFieldsTypes {
  fahrenheit = 'fahrenheit',
  celsius = 'celsius',
}

const state = reactive({
  fahrenheit: '' as string | number,
  celsius: '' as string | number,
  inputFields: [] as string[],
  outputFields: [] as string[],
});

const calculatorFieldsList: CalculatorFieldsTypes[] = [CalculatorFieldsTypes.fahrenheit, CalculatorFieldsTypes.celsius];
const calcType: string = 'temperature-conversion-calculator';
const minInputFieldsQty: number = 1;

const updateInputAndOutputFields = (field: CalculatorFieldsTypes, fieldValue: string | number) => {
  CalculatorsHelper.generateInputAndOutputFields(state.inputFields, state.outputFields, field, fieldValue, calcType, minInputFieldsQty);
  state.inputFields = CalculatorsHelper.getInputFields();
  state.outputFields = CalculatorsHelper.getOutputFields();
};

const setOutputFields = () => {
  calculatorFieldsList.forEach((field) => {
    state.outputFields.push(field);
  });
};

const resetAction = () => {
  state.fahrenheit = '';
  state.celsius = '';
  state.inputFields = [];
  state.outputFields = [];

  setOutputFields();
};

const calculateAction = () => {
  if (state.inputFields.length === minInputFieldsQty) {
    const fahrenheitValue = Number(state.fahrenheit);
    const celsiusValue = Number(state.celsius);

    const firstOutputField = state.outputFields[0];

    if (firstOutputField === CalculatorFieldsTypes.fahrenheit) {
      state.fahrenheit = GlobalHelper.roundToDecimals((celsiusValue * 9) / 5 + 32, 4);
    } else {
      state.celsius = GlobalHelper.roundToDecimals(((fahrenheitValue - 32) * 5) / 9, 4);
    }
  }
};

onMounted(() => {
  setOutputFields();
});
</script>
